import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconButton } from "@/components/IconButton";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import {
  addToWatchlist,
  analyzeItemSkipMarkers,
  createPlaybackSession,
  downloadExternalSubtitle,
  getConfig,
  getJob,
  getItem,
  getItemCast,
  getItemChildren,
  getItems,
  getSimilarItems,
  getWatchStates,
  getWatchlist,
  refreshItem,
  markItemBad,
  removeFromWatchlist,
  searchExternalSubtitles,
  searchItemMetadata,
  updateItem,
} from "@/api/endpoints";
import { artworkUrl } from "@/api/client";
import { formatByteSize } from "@/lib/byteSize";
import { isStandaloneDisplay } from "@/lib/displayMode";
import { formatReleaseDate } from "@/lib/dates";
import { itemArtworkSrc } from "@/lib/artwork";
import { chooseResumePositionMS } from "@/lib/localPlaybackPosition";
import { formatDurationMS, formatEpisodeCode, formatSeasonDisplayLabel, formatSeasonLabelParts } from "@/lib/mediaFormat";
import { MediaArtworkFrame, MediaCard } from "@/components/MediaCard";
import { ScrollRow } from "@/components/ScrollRow";
import { RatingBadge } from "@/components/RatingBadge";
import { WatchToggleButton } from "@/components/WatchToggleButton";
import type { CastMember, CollectionResponse, Item, Job, MetadataMatch, SubtitleProviderResult } from "@/types/api";
import type { RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  bestWatchStateCardProgressForItems,
  buildWatchStateResourceMap,
  formatPlaybackPosition,
  watchStateCardProgress,
  type CardWatchProgress,
} from "@/lib/watchState";
import {
  clearItemPlaybackState,
  markItemPlayed,
} from "@/lib/watchPlayback";

function isTvItem(item: Item | null): item is Item {
  return item !== null && ["show", "season", "episode"].includes(item.attributes.item_type);
}

function isDownloadableItem(item: Item): boolean {
  return ["movie", "episode", "track", "audiobook"].includes(item.attributes.item_type);
}

function isWatchlistableItem(item: Item): boolean {
  return ["movie", "episode"].includes(item.attributes.item_type);
}

function uniqueItemIds(itemIds: Array<string | null | undefined>): string[] {
  return Array.from(new Set(itemIds.filter((itemId): itemId is string => Boolean(itemId))));
}

function seriesHrefForItem(item: Item): string | null {
  if (item.attributes.item_type === "show") {
    return `/items/${item.id}`;
  }
  return item.attributes.series_id ? `/items/${item.attributes.series_id}` : null;
}

function seasonHrefForItem(item: Item): string | null {
  if (item.attributes.item_type === "season") {
    return `/items/${item.id}`;
  }
  return item.attributes.season_id ? `/items/${item.attributes.season_id}` : null;
}

function sortEpisodes(items: Item[]): Item[] {
  return [...items].sort((a, b) => {
    const seasonDelta = (a.attributes.season_number ?? Number.MAX_SAFE_INTEGER)
      - (b.attributes.season_number ?? Number.MAX_SAFE_INTEGER);
    if (seasonDelta !== 0) return seasonDelta;

    const episodeDelta = (a.attributes.episode_number ?? Number.MAX_SAFE_INTEGER)
      - (b.attributes.episode_number ?? Number.MAX_SAFE_INTEGER);
    if (episodeDelta !== 0) return episodeDelta;

    return a.attributes.title.localeCompare(b.attributes.title);
  });
}

function normalizeComparable(value: string | null | undefined): string | null {
  return value ? value.trim().toLowerCase() : null;
}

function matchesSeries(target: Item, candidate: Item): boolean {
  const targetAttrs = target.attributes;
  const candidateAttrs = candidate.attributes;

  if (candidateAttrs.item_type !== "episode") return false;

  const targetSeriesId = targetAttrs.series_id ?? (targetAttrs.item_type === "show" ? target.id : null);
  if (targetSeriesId && candidateAttrs.series_id) {
    return targetSeriesId === candidateAttrs.series_id;
  }

  const targetSeriesTitle = normalizeComparable(
    targetAttrs.series_title ?? (targetAttrs.item_type === "show" ? targetAttrs.title : null),
  );
  const candidateSeriesTitle = normalizeComparable(candidateAttrs.series_title);
  if (targetSeriesTitle && candidateSeriesTitle) {
    return targetSeriesTitle === candidateSeriesTitle;
  }

  return false;
}

async function getEpisodeCollection(item: Item | null): Promise<CollectionResponse<Item> | null> {
  if (!isTvItem(item)) return null;

  const libraryId = item.relationships.library.data.id;
  const itemType = item.attributes.item_type;
  const seriesId = item.attributes.series_id ?? (itemType === "show" ? item.id : null);
  const seriesTitle = item.attributes.series_title ?? (item.attributes.item_type === "show" ? item.attributes.title : null);
  const seasonId = itemType === "season" ? item.id : item.attributes.season_id;
  const seasonNumber = itemType === "season" || itemType === "episode" ? item.attributes.season_number : null;
  const collectionItemType = itemType === "show" ? "season" : "episode";
  const merged: Item[] = [];
  let cursor: string | undefined;
  let pageLimit = 100;

  do {
    const response = await getItems({
      limit: pageLimit,
      cursor,
      sort: "title",
      "filter[library_id]": libraryId,
      "filter[item_type]": collectionItemType,
      "filter[series_id]": seriesId ?? undefined,
      "filter[series_title]": seriesId ? undefined : seriesTitle ?? undefined,
      "filter[season_id]": seasonId ?? undefined,
      "filter[season_number]": seasonId || seasonNumber == null ? undefined : String(seasonNumber),
    });
    merged.push(...response.data);
    cursor = response.page.next_cursor ?? undefined;
    pageLimit = response.page.limit;
  } while (cursor);

  return {
    data: merged,
    page: {
      limit: pageLimit,
      next_cursor: null,
    },
    meta: {},
  };
}

interface EpisodeSeasonGroup {
  key: string;
  castItemId: string;
  routeItemId: string | null;
  item: Item | null;
  seasonNumber: number | null;
  /** "Season 3 · Villains / Fugitives" — headings, card titles, accessible names. */
  label: string;
  /** "Season 3" — the part that identifies the season in a chip. */
  numberLabel: string | null;
  /** "Villains / Fugitives" — shown beside the number when it adds something. */
  nameLabel: string | null;
  episodeCount: number;
  firstEpisodeId: string | null;
  episodes: Item[];
}

function buildSeasonGroups(item: Item, episodeCollection: CollectionResponse<Item> | null): EpisodeSeasonGroup[] {
  if (!isTvItem(item)) return [];

  const fetchedItems = episodeCollection?.data ?? [];
  const fetchedSeasons = fetchedItems.filter((candidate) => candidate.attributes.item_type === "season");
  if (item.attributes.item_type === "show" && fetchedSeasons.length > 0) {
    return fetchedSeasons.map((season) => {
      const seasonNumber = season.attributes.season_number ?? null;
      const labelParts = formatSeasonLabelParts(
        seasonNumber,
        season.attributes.season_title ?? season.attributes.title,
      );
      return {
        key: season.id,
        castItemId: season.id,
        routeItemId: season.id,
        item: season,
        seasonNumber,
        label: labelParts.full,
        numberLabel: labelParts.number,
        nameLabel: labelParts.name,
        episodeCount: season.attributes.episode_count ?? 0,
        firstEpisodeId: season.attributes.first_episode_id ?? null,
        episodes: [],
      };
    }).sort((a, b) => {
      if (a.seasonNumber == null && b.seasonNumber == null) return a.label.localeCompare(b.label);
      if (a.seasonNumber == null) return 1;
      if (b.seasonNumber == null) return -1;
      return a.seasonNumber - b.seasonNumber;
    });
  }

  const fetchedEpisodes = fetchedItems.filter((candidate) => candidate.attributes.item_type === "episode");
  const matchingEpisodes = fetchedEpisodes.filter((candidate) => matchesSeries(item, candidate));
  const seededEpisodes = matchingEpisodes.length > 0
    ? matchingEpisodes
    : item.attributes.item_type === "episode"
      ? [item]
      : [];

  const grouped = new Map<string, EpisodeSeasonGroup>();

  for (const episode of sortEpisodes(seededEpisodes)) {
    const seasonNumber = episode.attributes.season_number ?? null;
    const seasonKey = episode.attributes.season_id
      ?? (seasonNumber != null ? `season-${seasonNumber}` : "season-specials");

    const existing = grouped.get(seasonKey);
    if (existing) {
      existing.episodes.push(episode);
      existing.episodeCount += 1;
      continue;
    }

    const labelParts = formatSeasonLabelParts(seasonNumber, episode.attributes.season_title);
    grouped.set(seasonKey, {
      key: seasonKey,
      castItemId: episode.attributes.season_id ?? episode.id,
      routeItemId: episode.attributes.season_id ?? null,
      item: null,
      seasonNumber,
      label: labelParts.full,
      numberLabel: labelParts.number,
      nameLabel: labelParts.name,
      episodeCount: 1,
      firstEpisodeId: episode.id,
      episodes: [episode],
    });
  }

  return [...grouped.values()].sort((a, b) => {
    if (a.seasonNumber == null && b.seasonNumber == null) return a.label.localeCompare(b.label);
    if (a.seasonNumber == null) return 1;
    if (b.seasonNumber == null) return -1;
    return a.seasonNumber - b.seasonNumber;
  });
}

function getDefaultSeasonKey(item: Item, seasons: EpisodeSeasonGroup[], seriesContext: boolean): string | null {
  if (seriesContext) {
    return seasons[0]?.key ?? null;
  }

  const currentSeasonId = item.attributes.season_id;
  if (currentSeasonId && seasons.some((season) => season.key === currentSeasonId)) {
    return currentSeasonId;
  }

  const currentSeasonNumber = item.attributes.season_number;
  if (currentSeasonNumber != null) {
    const matched = seasons.find((season) => season.seasonNumber === currentSeasonNumber);
    if (matched) return matched.key;
  }

  return seasons[0]?.key ?? null;
}

function initialsForName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  const last = parts[parts.length - 1] ?? first;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function CastRail({ members }: { members: CastMember[] }) {
  if (members.length === 0) return null;

  return (
    <section className="cast-section">
      <div className="item-section-heading">
        <div>
          <h2 className="item-section-title">Main Characters</h2>
          <p className="item-section-subtitle">Top-billed cast for this title.</p>
        </div>
      </div>

      <div className="cast-row" role="list" aria-label="Main characters">
        {members.map((member) => {
          const { name, character, profile_url: profileUrl } = member.attributes;

          return (
            <article key={member.id} className="cast-card" role="listitem">
              <div className="cast-photo-wrap">
                {profileUrl ? (
                  <img className="cast-photo" src={profileUrl} alt={name} loading="lazy" />
                ) : (
                  <div className="cast-photo cast-photo--placeholder" aria-hidden="true">
                    {initialsForName(name)}
                  </div>
                )}
              </div>
              <div className="cast-copy">
                <div className="cast-name">{name}</div>
                {character && <div className="cast-character">{character}</div>}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function EpisodeCard({
  episode,
  isActiveEpisode,
  watched,
  progress,
  watchUpdating,
  canToggleWatched,
  starting,
  onPlay,
  onToggleWatched,
}: {
  episode: Item;
  isActiveEpisode: boolean;
  watched: boolean;
  progress?: CardWatchProgress;
  watchUpdating: boolean;
  canToggleWatched: boolean;
  starting: boolean;
  onPlay: () => void;
  onToggleWatched: () => void;
}) {
  const episodeAttrs = episode.attributes;
  const episodeCode = formatEpisodeCode(episode);
  const releaseDate = formatReleaseDate(episodeAttrs.release_date);

  return (
    <article className={`episode-card${isActiveEpisode ? " episode-card--active" : ""}`}>
      <div className="episode-card-media">
        <MediaArtworkFrame
          imageSrc={itemArtworkSrc(episode, "backdrop", { width: 640, format: "webp" })}
          imageAlt={episodeAttrs.title}
          detailsHref={`/items/${episode.id}`}
          playLabel={`Play ${episodeAttrs.title}`}
          playDisabled={starting}
          shape="backdrop"
          ratingItem={episode}
          progress={watched ? undefined : progress?.progress}
          progressLabel={watched ? undefined : progress?.label}
          progressTitle={watched ? undefined : progress?.title}
          onPlay={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onPlay();
          }}
          overlayAction={canToggleWatched && (
            <WatchToggleButton
              cardOverlay
              wrapperClassName="media-card-watch-toggle-wrap"
              className={`media-card-watch-toggle${watched ? " media-card-watch-toggle--watched" : ""}${watchUpdating ? " media-card-watch-toggle--pending" : ""}`}
              watched={watched}
              updating={watchUpdating}
              watchedLabel="Mark as watched"
              unwatchedLabel="Mark as unwatched"
              watchedAccessibleName={`Mark ${episodeAttrs.title} as watched`}
              unwatchedAccessibleName={`Mark ${episodeAttrs.title} as unwatched`}
              onToggle={onToggleWatched}
            />
          )}
        >
          <span className="episode-card-thumb-gradient" aria-hidden="true" />
          {episodeCode && <span className="episode-card-code-badge">{episodeCode}</span>}
        </MediaArtworkFrame>
      </div>

      <div className="episode-card-body">
        <div className="episode-card-header">
          <Link className="episode-card-title" to={`/items/${episode.id}`}>
            {episodeAttrs.title}
          </Link>

          <button
            type="button"
            className="btn btn--ghost episode-card-play"
            disabled={starting}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onPlay();
            }}
          >
            Play
          </button>
        </div>

        <div className="episode-card-meta">
          {releaseDate && <span>{releaseDate}</span>}
          {episodeAttrs.duration_ms && <span>{formatDurationMS(episodeAttrs.duration_ms)}</span>}
          {!releaseDate && episodeAttrs.year && <span>{episodeAttrs.year}</span>}
        </div>

        {episodeAttrs.overview && (
          <p className="episode-card-overview">{episodeAttrs.overview}</p>
        )}
      </div>
    </article>
  );
}

function SeasonDirectoryCard({
  season,
  watched,
  progress,
  watchUpdating,
  canToggleWatched,
  starting,
  onPlay,
  onToggleWatched,
}: {
  season: EpisodeSeasonGroup;
  watched: boolean;
  progress?: CardWatchProgress;
  watchUpdating: boolean;
  canToggleWatched: boolean;
  starting: boolean;
  onPlay: () => void;
  onToggleWatched: () => void;
}) {
  const destination = season.routeItemId ? `/items/${season.routeItemId}` : null;
  const episodeCount = season.episodeCount;
  const cardSummary = `${episodeCount} episode${episodeCount === 1 ? "" : "s"}`;
  const cardTitle = season.label;
  const backdropSource = season.routeItemId ?? season.episodes[0]?.id ?? season.castItemId;
  const primaryEpisode = season.episodes[0];
  const ratingItem = season.item ?? primaryEpisode ?? null;
  const backdropSrc = primaryEpisode
    ? itemArtworkSrc(primaryEpisode, "backdrop", { width: 960, format: "webp" })
    : artworkUrl(backdropSource, "backdrop", { width: 960, format: "webp" });

  return (
    <article className="season-directory-card">
      <div className="season-directory-card-media">
        <MediaArtworkFrame
          imageSrc={backdropSrc}
          imageAlt={cardTitle}
          detailsHref={destination}
          playLabel={`Play ${cardTitle}`}
          playDisabled={starting || !season.firstEpisodeId}
          shape="backdrop"
          ratingItem={ratingItem}
          progress={watched ? undefined : progress?.progress}
          progressLabel={watched ? undefined : progress?.label}
          progressTitle={watched ? undefined : progress?.title}
          onPlay={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onPlay();
          }}
          overlayAction={canToggleWatched && (
            <WatchToggleButton
              cardOverlay
              wrapperClassName="media-card-watch-toggle-wrap"
              className={`media-card-watch-toggle${watched ? " media-card-watch-toggle--watched" : ""}${watchUpdating ? " media-card-watch-toggle--pending" : ""}`}
              watched={watched}
              updating={watchUpdating}
              watchedLabel="Mark as watched"
              unwatchedLabel="Mark as unwatched"
              watchedAccessibleName={`Mark ${cardTitle} as watched`}
              unwatchedAccessibleName={`Mark ${cardTitle} as unwatched`}
              onToggle={onToggleWatched}
            />
          )}
        >
          <span className="season-directory-card-gradient" aria-hidden="true" />
          <span className="season-directory-card-count">{cardSummary}</span>
        </MediaArtworkFrame>
      </div>

      <div className="season-directory-card-body">
        {destination ? (
          <Link className="season-directory-card-title" to={destination}>
            {cardTitle}
          </Link>
        ) : (
          <h3 className="season-directory-card-title">{cardTitle}</h3>
        )}
        <p className="season-directory-card-meta">{cardSummary}</p>
        <div className="season-directory-card-actions">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={starting || !season.firstEpisodeId}
            onClick={onPlay}
          >
            {starting ? "Starting..." : "Play Season"}
          </button>
        </div>
      </div>
    </article>
  );
}

function SeasonDirectorySection({
  seasons,
  watchUpdatingKey,
  starting,
  onPlaySeason,
  onToggleSeasonWatched,
  isSeasonWatched,
  canToggleSeasonWatched,
  seasonProgress,
}: {
  seasons: EpisodeSeasonGroup[];
  watchUpdatingKey: string | null;
  starting: boolean;
  onPlaySeason: (season: EpisodeSeasonGroup) => void;
  onToggleSeasonWatched: (season: EpisodeSeasonGroup) => void;
  isSeasonWatched: (season: EpisodeSeasonGroup) => boolean;
  canToggleSeasonWatched: (season: EpisodeSeasonGroup) => boolean;
  seasonProgress: (season: EpisodeSeasonGroup) => CardWatchProgress | undefined;
}) {
  return (
    <section className="item-tv-panel">
      <div className="item-section-heading">
        <div>
          <h2 className="item-section-title">Seasons</h2>
          <p className="item-section-subtitle">
            {seasons.length > 0
              ? `${seasons.length} season${seasons.length === 1 ? "" : "s"} available`
              : "Season navigation appears after TV metadata is available."}
          </p>
        </div>
      </div>

      {seasons.length > 0 ? (
        <div className="season-directory-grid">
          {seasons.map((season) => (
            <SeasonDirectoryCard
              key={season.key}
              season={season}
              watched={isSeasonWatched(season)}
              progress={seasonProgress(season)}
              watchUpdating={watchUpdatingKey === `season:${season.key}`}
              canToggleWatched={canToggleSeasonWatched(season)}
              starting={starting}
              onPlay={() => onPlaySeason(season)}
              onToggleWatched={() => onToggleSeasonWatched(season)}
            />
          ))}
        </div>
      ) : (
        <div className="item-empty-state">No seasons are available for this series yet.</div>
      )}
    </section>
  );
}

function EpisodeNavigationSection({
  previousEpisode,
  nextEpisode,
  seasonPageHref,
}: {
  previousEpisode: Item | null;
  nextEpisode: Item | null;
  seasonPageHref: string | null;
}) {
  return (
    <section className="item-tv-panel">
      <div className="item-section-heading">
        <div>
          <h2 className="item-section-title">Episode Navigation</h2>
          <p className="item-section-subtitle">
            {seasonPageHref ? "Jump back to the season or move to the next episode." : "Move through this series one episode at a time."}
          </p>
        </div>
        {seasonPageHref && (
          <Link className="btn btn--secondary" to={seasonPageHref}>
            Open Season
          </Link>
        )}
      </div>

      {(previousEpisode || nextEpisode) ? (
        <div className="episode-nav">
          {previousEpisode ? (
            <Link className="episode-nav-link" to={`/items/${previousEpisode.id}`}>
              <span className="episode-nav-label">Previous</span>
              <span>{formatEpisodeCode(previousEpisode) ?? previousEpisode.attributes.title}</span>
            </Link>
          ) : (
            <span />
          )}

          {nextEpisode ? (
            <Link className="episode-nav-link episode-nav-link--next" to={`/items/${nextEpisode.id}`}>
              <span className="episode-nav-label">Next</span>
              <span>{formatEpisodeCode(nextEpisode) ?? nextEpisode.attributes.title}</span>
            </Link>
          ) : (
            <span />
          )}
        </div>
      ) : (
        <div className="item-empty-state">No adjacent episodes are available for this title.</div>
      )}
    </section>
  );
}

// The number leads and the name follows in a dimmer weight, so a row of chips can
// be read as "2, 3, 4" at a glance and still say which volume each season is.
function SeasonChipLabel({ season }: { season: EpisodeSeasonGroup }) {
  if (!season.numberLabel) return <>{season.label}</>;
  return (
    <>
      <span className="season-chip-number">{season.numberLabel}</span>
      {/* Separator lives in the text, not in CSS, so the chip's accessible name
          stays identical to season.label. */}
      {season.nameLabel && <span className="season-chip-name">{` · ${season.nameLabel}`}</span>}
    </>
  );
}

function EpisodeCollectionSection({
  title,
  subtitle,
  seasons,
  activeSeason,
  activeEpisodeId,
  watchUpdatingKey,
  episodeCollectionLoading,
  episodeCollectionError,
  seasonSelectorRef,
  onScrollSeasons,
  onPlayEpisode,
  onToggleEpisodeWatched,
  isEpisodeWatched,
  canToggleEpisodeWatched,
  episodeProgress,
  starting,
}: {
  title: string;
  subtitle: string;
  seasons: EpisodeSeasonGroup[];
  activeSeason: EpisodeSeasonGroup | null;
  activeEpisodeId?: string | null;
  watchUpdatingKey: string | null;
  episodeCollectionLoading: boolean;
  episodeCollectionError: boolean;
  seasonSelectorRef: RefObject<HTMLDivElement | null>;
  onScrollSeasons: (direction: "left" | "right") => void;
  onPlayEpisode: (episodeId: string) => void;
  onToggleEpisodeWatched: (episode: Item) => void;
  isEpisodeWatched: (episode: Item) => boolean;
  canToggleEpisodeWatched: (episode: Item) => boolean;
  episodeProgress: (episode: Item) => CardWatchProgress | undefined;
  starting: boolean;
}) {
  return (
    <section className="item-tv-panel">
      <div className="item-section-heading">
        <div>
          <h2 className="item-section-title">{title}</h2>
          <p className="item-section-subtitle">{subtitle}</p>
        </div>

        {seasons.length > 1 && (
          <div className="season-selector-wrap">
            <button
              type="button"
              className="season-scroll-arrow"
              onClick={() => onScrollSeasons("left")}
              aria-label="Scroll seasons left"
            >
              ‹
            </button>
            <div className="season-selector" role="tablist" aria-label="Seasons" ref={seasonSelectorRef}>
              {seasons.map((season) => (
                season.routeItemId ? (
                  <Link
                    key={season.key}
                    className={`season-chip${season.key === activeSeason?.key ? " season-chip--active" : ""}`}
                    to={`/items/${season.routeItemId}`}
                  >
                    <SeasonChipLabel season={season} />
                  </Link>
                ) : (
                  <span
                    key={season.key}
                    className={`season-chip${season.key === activeSeason?.key ? " season-chip--active" : ""}`}
                  >
                    <SeasonChipLabel season={season} />
                  </span>
                )
              ))}
            </div>
            <button
              type="button"
              className="season-scroll-arrow"
              onClick={() => onScrollSeasons("right")}
              aria-label="Scroll seasons right"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {episodeCollectionLoading && seasons.length === 0 && (
        <div className="item-empty-state">Loading episode navigation...</div>
      )}

      {episodeCollectionError && (
        <div className="item-empty-state">Episode navigation is unavailable right now.</div>
      )}

      {!episodeCollectionLoading && !episodeCollectionError && activeSeason && (
        <div className="episode-card-grid">
          {activeSeason.episodes.map((episode) => {
            const watched = isEpisodeWatched(episode);
            return (
              <EpisodeCard
                key={episode.id}
                episode={episode}
                isActiveEpisode={episode.id === activeEpisodeId}
                watched={watched}
                progress={watched ? undefined : episodeProgress(episode)}
                watchUpdating={watchUpdatingKey === `episode:${episode.id}`}
                canToggleWatched={canToggleEpisodeWatched(episode)}
                starting={starting}
                onPlay={() => onPlayEpisode(episode.id)}
                onToggleWatched={() => onToggleEpisodeWatched(episode)}
              />
            );
          })}
        </div>
      )}

      {!episodeCollectionLoading && !episodeCollectionError && !activeSeason && (
        <div className="item-empty-state">No episode context is available for this item yet.</div>
      )}
    </section>
  );
}

function emptyItemCollection(): CollectionResponse<Item> {
  return {
    data: [],
    page: {
      limit: 100,
      next_cursor: null,
    },
    meta: {},
  };
}

function localMediaKind(item: Item): string {
  return item.attributes.local_media_kind ?? "";
}

function localMediaLabel(item: Item, fallback: string): string {
  const label = item.attributes.local_media_label?.trim();
  return label || fallback;
}

function sortLocalMediaItems(items: Item[]): Item[] {
  return [...items].sort((a, b) => {
    const sortDelta = (a.attributes.local_media_sort ?? Number.MAX_SAFE_INTEGER)
      - (b.attributes.local_media_sort ?? Number.MAX_SAFE_INTEGER);
    if (sortDelta !== 0) return sortDelta;
    return a.attributes.title.localeCompare(b.attributes.title);
  });
}

function localMediaDiagnosticsFor(items: Item[]): string[] {
  return Array.from(new Set(items.flatMap((item) => item.attributes.local_media_diagnostics ?? [])));
}

function localMediaDiagnosticLabel(code: string): string {
  switch (code) {
    case "ambiguous_local_grouping":
      return "Ambiguous local grouping detected. Check this movie's file names or folders before relying on automatic grouping.";
    default:
      return code.replaceAll("_", " ");
  }
}

function LocalMediaOption({
  item,
  label,
  starting,
  onPlay,
}: {
  item: Item;
  label: string;
  starting: boolean;
  onPlay: (itemId: string) => void;
}) {
  const attrs = item.attributes;
  const releaseDate = formatReleaseDate(attrs.release_date);
  const meta = [
    attrs.duration_ms ? formatDurationMS(attrs.duration_ms) : null,
    releaseDate ?? (attrs.year ? String(attrs.year) : null),
  ].filter((value): value is string => Boolean(value));

  return (
    <article className="local-media-option">
      <img
        className="local-media-option-art"
        src={itemArtworkSrc(item, "poster", { width: 120, format: "webp" })}
        alt={attrs.title}
        loading="lazy"
        decoding="async"
      />
      <div className="local-media-option-copy">
        <h3 className="local-media-option-title">{label}</h3>
        {attrs.title !== label && <p className="local-media-option-subtitle">{attrs.title}</p>}
        {meta.length > 0 && <p className="local-media-option-meta">{meta.join(" • ")}</p>}
      </div>
      <button
        type="button"
        className="btn btn--secondary local-media-option-play"
        disabled={starting}
        onClick={() => onPlay(item.id)}
      >
        {starting ? "Starting..." : "Play"}
      </button>
    </article>
  );
}

function LocalMediaSection({
  item,
  children,
  loading,
  error,
  starting,
  sectionRef,
  onPlay,
}: {
  item: Item;
  children: Item[];
  loading: boolean;
  error: Error | null;
  starting: boolean;
  sectionRef: RefObject<HTMLElement | null>;
  onPlay: (itemId: string) => void;
}) {
  const parts = sortLocalMediaItems(children.filter((child) => localMediaKind(child) === "part"));
  const versions = sortLocalMediaItems(children.filter((child) => localMediaKind(child) === "version"));
  const extras = sortLocalMediaItems(children.filter((child) => localMediaKind(child) === "extra"));
  const diagnostics = localMediaDiagnosticsFor([item, ...children]);
  const hasLocalMedia = parts.length > 0 || versions.length > 0 || extras.length > 0 || diagnostics.length > 0;

  if (!loading && !error && !hasLocalMedia) {
    return null;
  }

  const primaryLabel = localMediaLabel(
    item,
    parts.length > 0 ? "Part 1" : versions.length > 0 ? "Primary Version" : "Feature",
  );

  return (
    <section className="item-local-media-panel" ref={sectionRef}>
      <div className="item-section-heading">
        <div>
          <h2 className="item-section-title">Local Media</h2>
          <p className="item-section-subtitle">
            {parts.length > 0
              ? "Multipart files are grouped under this movie."
              : versions.length > 0
                ? "Choose which local version to play."
                : extras.length > 0
                  ? "Related extras are grouped under this movie."
                  : "Grouping details for this movie."}
          </p>
        </div>
      </div>

      {loading && !hasLocalMedia && (
        <div className="item-empty-state">Loading local media...</div>
      )}

      {error && (
        <div className="item-empty-state">Local media grouping is unavailable right now.</div>
      )}

      {diagnostics.length > 0 && (
        <div className="local-media-diagnostics">
          {diagnostics.map((diagnostic) => (
            <span key={diagnostic}>{localMediaDiagnosticLabel(diagnostic)}</span>
          ))}
        </div>
      )}

      {versions.length > 0 && (
        <div className="local-media-block">
          <h3 className="local-media-heading">Available Versions</h3>
          <div className="local-media-option-list">
            <LocalMediaOption
              item={item}
              label={primaryLabel}
              starting={starting}
              onPlay={onPlay}
            />
            {versions.map((version) => (
              <LocalMediaOption
                key={version.id}
                item={version}
                label={localMediaLabel(version, version.attributes.title)}
                starting={starting}
                onPlay={onPlay}
              />
            ))}
          </div>
        </div>
      )}

      {parts.length > 0 && (
        <div className="local-media-block">
          <h3 className="local-media-heading">Movie Parts</h3>
          <div className="local-media-option-list">
            <LocalMediaOption
              item={item}
              label={primaryLabel}
              starting={starting}
              onPlay={onPlay}
            />
            {parts.map((part) => (
              <LocalMediaOption
                key={part.id}
                item={part}
                label={localMediaLabel(part, part.attributes.title)}
                starting={starting}
                onPlay={onPlay}
              />
            ))}
          </div>
        </div>
      )}

      {extras.length > 0 && (
        <div className="local-media-block">
          <h3 className="local-media-heading">Extras</h3>
          <div className="item-grid local-media-extra-grid">
            {extras.map((extra) => (
              <MediaCard key={extra.id} item={extra} shape="backdrop" />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MoreLikeThisSection({
  items,
}: {
  items: Item[];
}) {
  if (items.length === 0) return null;

  return (
    <ScrollRow
      title="More like this"
      expandable
      expandedMinCardWidth="170px"
    >
      {items.map((item) => (
        <MediaCard key={item.id} item={item} />
      ))}
    </ScrollRow>
  );
}

export function ItemPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDownload = Boolean(user?.attributes.can_download);
  const [starting, setStarting] = useState(false);
  const [activeSeasonKey, setActiveSeasonKey] = useState<string | null>(null);
  const [metadataEditorOpen, setMetadataEditorOpen] = useState(false);
  const [metadataSearchQuery, setMetadataSearchQuery] = useState("");
  const [metadataTitle, setMetadataTitle] = useState("");
  const [metadataOverview, setMetadataOverview] = useState("");
  const [metadataYear, setMetadataYear] = useState("");
  const [metadataReleaseDate, setMetadataReleaseDate] = useState("");
  const [metadataTMDbID, setMetadataTMDbID] = useState("");
  const [metadataMatches, setMetadataMatches] = useState<MetadataMatch[]>([]);
  const [metadataSearching, setMetadataSearching] = useState(false);
  const [metadataSaving, setMetadataSaving] = useState(false);
  const [metadataMessage, setMetadataMessage] = useState<string | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanTargetId, setScanTargetId] = useState<string | null>(null);
  const [markBadMessage, setMarkBadMessage] = useState<string | null>(null);
  const [markBadError, setMarkBadError] = useState<string | null>(null);
  const [markBadPending, setMarkBadPending] = useState(false);
  const [watchMessage, setWatchMessage] = useState<string | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [watchUpdating, setWatchUpdating] = useState(false);
  const [watchUpdatingKey, setWatchUpdatingKey] = useState<string | null>(null);
  const [watchOverrides, setWatchOverrides] = useState<Record<string, boolean>>({});
  const [watchlistUpdating, setWatchlistUpdating] = useState(false);
  const [watchlistMessage, setWatchlistMessage] = useState<string | null>(null);
  const [watchlistError, setWatchlistError] = useState<string | null>(null);
  const seasonSelectorRef = useRef<HTMLDivElement>(null);
  const metadataSectionRef = useRef<HTMLElement>(null);
  const localMediaSectionRef = useRef<HTMLElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const autoplayHandledRef = useRef<string | null>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [subtitleDialogOpen, setSubtitleDialogOpen] = useState(false);
  const [subtitleResults, setSubtitleResults] = useState<SubtitleProviderResult[]>([]);
  const [subtitleLoading, setSubtitleLoading] = useState(false);
  const [subtitleError, setSubtitleError] = useState<string | null>(null);
  const [subtitleDownloadingId, setSubtitleDownloadingId] = useState<string | null>(null);
  const [subtitleStatus, setSubtitleStatus] = useState<string | null>(null);
  const [subtitleLanguage, setSubtitleLanguage] = useState("");
  const [downloadSizeBytes, setDownloadSizeBytes] = useState<number | null>(null);
  const [downloadStarted, setDownloadStarted] = useState(false);
  // iOS forbids downloads entirely in home-screen standalone mode — no Safari
  // chrome, no download manager — so the control has to behave differently
  // there. Lazy-initialized so it only ever evaluates client-side (#733).
  const [isStandalone] = useState(() => isStandaloneDisplay());

  const item = useApi(() => getItem(itemId!), [itemId]);
  const adminConfig = useApi(
    () => (user?.attributes.is_admin ? getConfig() : Promise.resolve(null)),
    [user?.attributes.is_admin],
  );
  const opensubtitlesEnabled = Boolean(adminConfig.data?.data?.attributes.opensubtitles_enabled)
    && Boolean(adminConfig.data?.data?.attributes.has_opensubtitles_api_key);
  const opensubtitlesDefaultLanguage = adminConfig.data?.data?.attributes.opensubtitles_default_language ?? "en";
  const watchlist = useApi(() => getWatchlist(), []);
  const episodeCollection = useApi(
    () => getEpisodeCollection(item.data?.data ?? null),
    [
      item.data?.data?.id,
      item.data?.data?.attributes.item_type,
      item.data?.data?.attributes.series_id,
      item.data?.data?.attributes.series_title,
      item.data?.data?.attributes.season_id,
      item.data?.data?.attributes.season_number,
      item.data?.data?.relationships.library.data.id,
    ],
  );
  const localMedia = useApi(
    () => {
      const loadedItem = item.data?.data ?? null;
      if (!loadedItem || loadedItem.attributes.item_type !== "movie") {
        return Promise.resolve(emptyItemCollection());
      }
      return getItemChildren(loadedItem.id);
    },
    [item.data?.data?.id, item.data?.data?.attributes.item_type],
  );
  const similarItems = useApi(
    () => {
      const loadedItem = item.data?.data ?? null;
      if (!loadedItem || !["movie", "show"].includes(loadedItem.attributes.item_type)) {
        return Promise.resolve(emptyItemCollection());
      }
      return getSimilarItems(loadedItem.id, { limit: 12 });
    },
    [item.data?.data?.id, item.data?.data?.attributes.item_type],
  );

  const autoplay = searchParams.get("autoplay") === "1";
  const seriesContext = searchParams.get("context") === "series";
  const currentItem = item.data?.data ?? null;
  const localMediaItems = useMemo(
    () => localMedia.data?.data ?? [],
    [localMedia.data],
  );
  const hasLocalVersionChoices = currentItem?.attributes.item_type === "movie"
    && localMediaItems.some((child) => localMediaKind(child) === "version");
  const hasLocalPartChoices = currentItem?.attributes.item_type === "movie"
    && localMediaItems.some((child) => localMediaKind(child) === "part");
  const hasLocalPlaybackChoices = hasLocalVersionChoices || hasLocalPartChoices;
  const downloadPath = canDownload && currentItem && isDownloadableItem(currentItem)
    ? currentItem.attributes.download_path ?? null
    : null;
  // The path carries a freshly minted scoped token on every item fetch, so it
  // is read through a ref: keying the effect on it would re-probe the size on
  // each refetch (#730).
  const downloadPathRef = useRef<string | null>(null);
  downloadPathRef.current = downloadPath;
  const hasDownloadPath = downloadPath !== null;
  const downloadLabel = downloadSizeBytes === null
    ? "Download"
    : `Download (${formatByteSize(downloadSizeBytes)})`;

  // Size the download before the user commits to it. Tapping Download starts a
  // multi-GB transfer, and without this the UI gives no hint of the cost (#730).
  useEffect(() => {
    setDownloadStarted(false);
    setDownloadSizeBytes(null);
    const path = downloadPathRef.current;
    if (!path) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(path, { method: "HEAD" });
        if (!response.ok) return;
        const length = Number(response.headers.get("content-length"));
        if (!cancelled && Number.isFinite(length) && length > 0) {
          setDownloadSizeBytes(length);
        }
      } catch {
        // Advisory only — the button still works without a size.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId, hasDownloadPath]);
  const currentItemWatchlisted = currentItem
    ? (watchlist.data?.data ?? []).some((entry) => entry.id === currentItem.id)
    : false;
  const canToggleWatchlist = currentItem !== null && isWatchlistableItem(currentItem);

  const seasons = useMemo(
    () => (currentItem ? buildSeasonGroups(currentItem, episodeCollection.data) : []),
    [currentItem, episodeCollection.data],
  );
  const defaultSeasonKey = useMemo(
    () => (currentItem ? getDefaultSeasonKey(currentItem, seasons, seriesContext) : null),
    [currentItem, seasons, seriesContext],
  );
  const activeSeason = useMemo(
    () => seasons.find((season) => season.key === activeSeasonKey) ?? seasons[0] ?? null,
    [activeSeasonKey, seasons],
  );
  const castItemId = useMemo(() => {
    if ((currentItem?.attributes.item_type === "show" || seriesContext) && activeSeason?.castItemId) {
      return activeSeason.castItemId;
    }
    return itemId ?? null;
  }, [activeSeason?.castItemId, currentItem?.attributes.item_type, itemId, seriesContext]);
  const cast = useApi(() => getItemCast(castItemId!), [castItemId]);
  const allEpisodes = useMemo(
    () => seasons.flatMap((season) => season.episodes),
    [seasons],
  );
  const currentEpisodeIndex = useMemo(
    () => currentItem ? allEpisodes.findIndex((episode) => episode.id === currentItem.id) : -1,
    [allEpisodes, currentItem],
  );
  const previousEpisode: Item | null = currentEpisodeIndex > 0 ? allEpisodes[currentEpisodeIndex - 1] ?? null : null;
  const nextEpisode: Item | null = currentEpisodeIndex >= 0 && currentEpisodeIndex < allEpisodes.length - 1
    ? allEpisodes[currentEpisodeIndex + 1] ?? null
    : null;
  const itemWatchIds = useMemo(
    () => uniqueItemIds([
      currentItem?.id,
      currentItem?.attributes.series_id,
      currentItem?.attributes.season_id,
      ...seasons.map((season) => season.routeItemId ?? null),
      ...allEpisodes.map((episode) => episode.id),
      ...localMediaItems.map((child) => child.id),
    ]),
    [allEpisodes, currentItem?.attributes.season_id, currentItem?.attributes.series_id, currentItem?.id, localMediaItems, seasons],
  );
  const watchStates = useApi(() => getWatchStates(itemWatchIds), [itemWatchIds.join("|")]);
  const watchStateByItem = useMemo(
    () => buildWatchStateResourceMap(watchStates.data?.data ?? []),
    [watchStates.data],
  );
  const currentItemWatchState = currentItem ? watchStateByItem.get(currentItem.id) ?? null : null;
  const seriesWatchState = currentItem?.attributes.series_id
    ? watchStateByItem.get(currentItem.attributes.series_id) ?? null
    : null;
  const watchState = seriesContext
    ? seriesWatchState ?? (currentItem?.attributes.item_type === "show" ? currentItemWatchState : null)
    : currentItemWatchState;
  const watchStatesInitialLoad = watchStates.loading && !watchStates.data;
  const playTargetId = currentItem?.attributes.item_type === "show" || currentItem?.attributes.item_type === "season"
    ? activeSeason?.firstEpisodeId ?? activeSeason?.episodes[0]?.id ?? null
    : currentItem?.id ?? null;

  const progressPercent = watchState && !watchState.attributes.watched ? watchState.attributes.progress_percent : 0;
  const serverResumePositionMS = watchState && !watchState.attributes.watched ? watchState.attributes.position_ms : 0;
  const resumePositionMS = watchState && !watchState.attributes.watched
    ? chooseResumePositionMS(
        serverResumePositionMS,
        watchState.attributes.updated_at,
        playTargetId,
      )
    : 0;
  const currentItemCompleted = watchState?.attributes.watched === true;
  const hasResume = !seriesContext && !currentItemCompleted && resumePositionMS > 0 && playTargetId === currentItem?.id;
  const metadataSearchTitle = currentItem?.attributes.series_title ?? currentItem?.attributes.title ?? "";
  const metadataTitleValue = currentItem?.attributes.title ?? "";
  const metadataOverviewValue = currentItem?.attributes.overview ?? "";
  const metadataYearValue = currentItem?.attributes.year ? String(currentItem.attributes.year) : "";
  const metadataReleaseDateValue = currentItem?.attributes.release_date ?? "";
  const metadataTMDbIDValue = currentItem?.attributes.tmdb_id ?? "";

  useEffect(() => {
    setActiveSeasonKey(defaultSeasonKey);
  }, [defaultSeasonKey]);

  useEffect(() => {
    if (!currentItem?.id) return;
    setMetadataSearchQuery(metadataSearchTitle);
    setMetadataTitle(metadataTitleValue);
    setMetadataOverview(metadataOverviewValue);
    setMetadataYear(metadataYearValue);
    setMetadataReleaseDate(metadataReleaseDateValue);
    setMetadataTMDbID(metadataTMDbIDValue);
    setMetadataMatches([]);
    setMetadataMessage(null);
    setMetadataError(null);
  }, [
    currentItem?.id,
    metadataOverviewValue,
    metadataReleaseDateValue,
    metadataSearchTitle,
    metadataTMDbIDValue,
    metadataTitleValue,
    metadataYearValue,
  ]);

  useEffect(() => {
    setScanMessage(null);
    setScanError(null);
    setScanTargetId(null);
    setWatchMessage(null);
    setWatchError(null);
    setWatchlistMessage(null);
    setWatchlistError(null);
  }, [currentItem?.id]);

  useEffect(() => {
    setWatchOverrides({});
  }, [watchStates.data]);

  function applyWatchOverrides(items: Item[], watched: boolean, extraItemIds: string[] = []) {
    setWatchOverrides((current) => {
      const next = { ...current };
      for (const item of items) {
        next[item.id] = watched;
      }
      for (const itemID of extraItemIds) {
        next[itemID] = watched;
      }
      return next;
    });
  }

  function isWatched(item: Item): boolean {
    if (item.id in watchOverrides) {
      return watchOverrides[item.id]!;
    }
    return watchStateByItem.get(item.id)?.attributes.watched === true;
  }

  async function handlePlay(
    targetItemId = playTargetId,
    options?: { replaceHistory?: boolean; startPositionMS?: number },
  ) {
    if (!targetItemId) return;
    setStarting(true);
    try {
      const session = await createPlaybackSession({
        item_id: targetItemId,
        profile: { container: "hls" },
        start_position_ms: options?.startPositionMS,
      });
      navigate(`/play/${session.data.id}`, { replace: options?.replaceHistory === true });
    } finally {
      setStarting(false);
    }
  }

  async function handleToggleWatchlist() {
    if (!currentItem || !canToggleWatchlist) {
      return;
    }
    setWatchlistUpdating(true);
    setWatchlistMessage(null);
    setWatchlistError(null);
    try {
      if (currentItemWatchlisted) {
        await removeFromWatchlist(currentItem.id);
        setWatchlistMessage("Removed from watchlist.");
      } else {
        await addToWatchlist(currentItem.id);
        setWatchlistMessage("Added to watchlist.");
      }
      await watchlist.refetch();
    } catch {
      setWatchlistError("Failed to update watchlist.");
    } finally {
      setWatchlistUpdating(false);
    }
  }

  async function handleMetadataSearch() {
    if (!itemId) return;
    setMetadataSearching(true);
    setMetadataError(null);
    setMetadataMessage(null);
    try {
      const response = await searchItemMetadata(itemId, {
        q: metadataSearchQuery.trim() || undefined,
      });
      setMetadataMatches(response.data);
    } catch {
      setMetadataError("Failed to search TMDb");
    } finally {
      setMetadataSearching(false);
    }
  }

  async function refreshMetadataViews() {
    await Promise.all([
      item.refetch(),
      cast.refetch(),
      episodeCollection.refetch(),
      localMedia.refetch(),
      watchStates.refetch(),
    ]);
  }

  async function handleApplyMetadataMatch(tmdbID: string) {
    if (!itemId) return;
    setMetadataSaving(true);
    setMetadataError(null);
    setMetadataMessage(null);
    try {
      const response = await updateItem(itemId, { tmdb_id: tmdbID });
      setMetadataTMDbID(response.data.attributes.tmdb_id ?? "");
      setMetadataTitle(response.data.attributes.title ?? "");
      setMetadataOverview(response.data.attributes.overview ?? "");
      setMetadataYear(response.data.attributes.year ? String(response.data.attributes.year) : "");
      setMetadataReleaseDate(response.data.attributes.release_date ?? "");
      setMetadataMessage("Match applied");
      await refreshMetadataViews();
    } catch {
      setMetadataError("Failed to apply TMDb match");
    } finally {
      setMetadataSaving(false);
    }
  }

  async function handleSaveMetadataOverrides() {
    if (!itemId) return;
    const parsedYear = metadataYear.trim() === "" ? 0 : Number.parseInt(metadataYear, 10);
    if (metadataYear.trim() !== "" && Number.isNaN(parsedYear)) {
      setMetadataError("Year must be a number");
      return;
    }
    const trimmedReleaseDate = metadataReleaseDate.trim();
    if (trimmedReleaseDate !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(trimmedReleaseDate)) {
      setMetadataError("Release / air date must be YYYY-MM-DD");
      return;
    }
    setMetadataSaving(true);
    setMetadataError(null);
    setMetadataMessage(null);
    try {
      await updateItem(itemId, {
        tmdb_id: metadataTMDbID.trim(),
        title: metadataTitle.trim(),
        overview: metadataOverview.trim(),
        year: parsedYear,
        release_date: trimmedReleaseDate,
      });
      setMetadataMessage("Overrides saved");
      await refreshMetadataViews();
    } catch {
      setMetadataError("Failed to save metadata overrides");
    } finally {
      setMetadataSaving(false);
    }
  }

  async function handleClearMetadataOverrides() {
    if (!itemId) return;
    setMetadataSaving(true);
    setMetadataError(null);
    setMetadataMessage(null);
    try {
      await updateItem(itemId, {
        tmdb_id: "",
        title: "",
        overview: "",
        year: 0,
        release_date: "",
      });
      setMetadataMessage("Overrides cleared");
      await refreshMetadataViews();
    } catch {
      setMetadataError("Failed to clear metadata overrides");
    } finally {
      setMetadataSaving(false);
    }
  }

  async function waitForJobCompletion(
    jobId: string,
    options: { maxAttempts?: number; onPoll?: (job: Job) => void } = {},
  ) {
    const maxAttempts = options.maxAttempts ?? 30;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const job = await getJob(jobId);
      options.onPoll?.(job.data);
      const state = job.data.attributes.state;
      if (state === "completed") {
        return;
      }
      if (state === "failed" || state === "cancelled") {
        throw new Error(`Scan ${state}`);
      }
      await wait(1000);
    }
    throw new Error("Scan timed out");
  }

  function formatSkipMarkerScanProgress(label: string, job: Job) {
    const progress = job.attributes.progress;
    if (progress && progress.total > 0) {
      return `${label} running... ${progress.percent}% (${progress.current}/${progress.total})`;
    }
    return `${label} running...`;
  }

  async function handleScopedScan(targetItemId: string, label: string) {
    setScanTargetId(targetItemId);
    setScanMessage(null);
    setScanError(null);
    try {
      const job = await refreshItem(targetItemId);
      await waitForJobCompletion(job.data.id);
      await refreshMetadataViews();
      setScanMessage(`${label} finished`);
    } catch (error) {
      setScanError(error instanceof Error ? error.message : `Failed to scan ${label.toLowerCase()}`);
    } finally {
      setScanTargetId(null);
    }
  }

  async function handleSkipMarkerScan(targetItemId: string, label: string) {
    setScanTargetId(`skip:${targetItemId}`);
    setScanMessage(null);
    setScanError(null);
    try {
      const job = await analyzeItemSkipMarkers(targetItemId);
      setScanMessage(`${label} started. Skip marker analysis can take a few minutes.`);
      await waitForJobCompletion(job.data.id, {
        maxAttempts: 900,
        onPoll: (runningJob) => setScanMessage(formatSkipMarkerScanProgress(label, runningJob)),
      });
      await refreshMetadataViews();
      setScanMessage(`${label} finished. Items with detected markers will show skip actions during playback.`);
    } catch (error) {
      setScanError(error instanceof Error ? error.message : `Failed to scan skip markers for ${label.toLowerCase()}`);
    } finally {
      setScanTargetId(null);
    }
  }

  async function handleTogglePlayed() {
    if (!currentItem || !canTogglePlayed) {
      return;
    }

    setWatchUpdating(true);
    setWatchUpdatingKey("page");
    setWatchMessage(null);
    setWatchError(null);

    try {
      if (allTargetsWatched) {
        await clearItemPlaybackState(watchToggleItemId);
        applyWatchOverrides(watchTargetItems, false, [currentItem.id, watchToggleItemId]);
        setWatchMessage(
          watchTargetItemCount > 1
            ? `Marked ${watchScopeLabel.toLowerCase()} as unwatched`
            : "Marked as unplayed",
        );
      } else {
        await markItemPlayed(watchToggleItemId);
        applyWatchOverrides(watchTargetItems, true, [currentItem.id, watchToggleItemId]);
        setWatchMessage(
          watchTargetItemCount > 1
            ? `Marked ${watchScopeLabel.toLowerCase()} as watched`
            : "Marked as played",
        );
      }
      void watchStates.refetch().catch(() => {});
    } catch {
      setWatchError(
        allTargetsWatched
          ? `Failed to clear ${watchScopeLabel.toLowerCase()} watched state`
          : `Failed to mark ${watchScopeLabel.toLowerCase()} as watched`,
      );
    } finally {
      setWatchUpdating(false);
      setWatchUpdatingKey(null);
    }
  }

  function isEpisodeWatched(episode: Item): boolean {
    return isWatched(episode);
  }

  function canToggleEpisodeWatched(episode: Item): boolean {
    return (watchStateByItem.get(episode.id)?.attributes.can_mark_watched ?? (episode.attributes.duration_ms ?? 0) > 0)
      && !watchStatesInitialLoad;
  }

  function episodeProgress(episode: Item): CardWatchProgress | undefined {
    return watchStateCardProgress(watchStateByItem.get(episode.id), episode);
  }

  function isSeasonWatched(season: EpisodeSeasonGroup): boolean {
    const seasonState = season.routeItemId ? watchStateByItem.get(season.routeItemId) : null;
    if (season.routeItemId && season.routeItemId in watchOverrides) {
      return watchOverrides[season.routeItemId]!;
    }
    if (seasonState) {
      return seasonState.attributes.watched;
    }
    return season.episodes.length > 0 && season.episodes.every((episode) => isEpisodeWatched(episode));
  }

  function canToggleSeasonWatched(season: EpisodeSeasonGroup): boolean {
    const seasonState = season.routeItemId ? watchStateByItem.get(season.routeItemId) : null;
    if (seasonState) {
      return !watchStatesInitialLoad;
    }
    return season.episodes.length > 0 && !watchStatesInitialLoad;
  }

  function seasonProgress(season: EpisodeSeasonGroup): CardWatchProgress | undefined {
    const direct = season.routeItemId
      ? watchStateCardProgress(watchStateByItem.get(season.routeItemId), season.item)
      : undefined;
    return direct ?? bestWatchStateCardProgressForItems(season.episodes, watchStateByItem);
  }

  async function handleToggleEpisodeWatched(episode: Item) {
    if (!canToggleEpisodeWatched(episode)) {
      setWatchError("Episode playback metadata is incomplete, so watched state cannot be updated yet.");
      return;
    }

    setWatchUpdating(true);
    setWatchUpdatingKey(`episode:${episode.id}`);
    setWatchMessage(null);
    setWatchError(null);

    try {
      if (isEpisodeWatched(episode)) {
        await clearItemPlaybackState(episode.id);
        applyWatchOverrides([episode], false);
        setWatchMessage("Marked episode as unwatched");
      } else {
        await markItemPlayed(episode.id);
        applyWatchOverrides([episode], true);
        setWatchMessage("Marked episode as watched");
      }
      void watchStates.refetch().catch(() => {});
    } catch {
      setWatchError("Failed to update episode watched state");
    } finally {
      setWatchUpdating(false);
      setWatchUpdatingKey(null);
    }
  }

  async function handleToggleSeasonWatched(season: EpisodeSeasonGroup) {
    if (!canToggleSeasonWatched(season)) {
      setWatchError("Season playback metadata is incomplete, so watched state cannot be updated yet.");
      return;
    }

    setWatchUpdating(true);
    setWatchUpdatingKey(`season:${season.key}`);
    setWatchMessage(null);
    setWatchError(null);

    try {
      const seasonItemId = season.routeItemId ?? season.episodes[0]?.attributes.season_id ?? null;
      if (!seasonItemId) {
        setWatchError("Season playback metadata is incomplete, so watched state cannot be updated yet.");
        return;
      }

      if (isSeasonWatched(season)) {
        await clearItemPlaybackState(seasonItemId);
        applyWatchOverrides(season.episodes, false, [seasonItemId]);
        setWatchMessage("Marked season as unwatched");
      } else {
        await markItemPlayed(seasonItemId);
        applyWatchOverrides(season.episodes, true, [seasonItemId]);
        setWatchMessage("Marked season as watched");
      }
      void watchStates.refetch().catch(() => {});
    } catch {
      setWatchError("Failed to update season watched state");
    } finally {
      setWatchUpdating(false);
      setWatchUpdatingKey(null);
    }
  }

  function scrollSeasonSelector(direction: "left" | "right") {
    const el = seasonSelectorRef.current;
    if (!el) return;
    const amount = Math.max(160, el.clientWidth * 0.75);
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }

  useEffect(() => {
    if (!autoplay) {
      autoplayHandledRef.current = null;
    }
  }, [autoplay]);

  useEffect(() => {
    if (!autoplay || !item.data || !playTargetId || starting) return;
    if (currentItem?.attributes.item_type === "movie" && localMedia.loading && !localMedia.data) return;
    if (autoplayHandledRef.current === playTargetId) return;
    autoplayHandledRef.current = playTargetId;
    if (hasLocalPlaybackChoices) {
      requestAnimationFrame(() => {
        localMediaSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }
    void handlePlay(playTargetId, { replaceHistory: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, currentItem?.attributes.item_type, hasLocalPlaybackChoices, item.data, localMedia.data, localMedia.loading, playTargetId, starting]);

  useEffect(() => {
    if (!actionsMenuOpen) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (!actionsMenuRef.current?.contains(event.target as Node)) {
        setActionsMenuOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setActionsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [actionsMenuOpen]);

  if (item.loading) return <div className="page-loading">Loading...</div>;
  if (item.error) return <p className="error-message">Item not found</p>;
  if (!item.data) return null;

  const { attributes: attrs } = item.data.data;
  const releaseDate = formatReleaseDate(attrs.release_date);
  const pageKind = seriesContext || attrs.item_type === "show"
    ? "show"
    : attrs.item_type === "season"
      ? "season"
      : attrs.item_type === "episode"
        ? "episode"
        : "movie";
  const itemCode = formatEpisodeCode(item.data.data);
  const primaryPlayLabel = pageKind === "show"
    ? "▶ Play Series"
    : hasResume
      ? "▶ Resume"
      : pageKind === "episode"
        ? "▶ Play Episode"
        : pageKind === "season"
          ? "▶ Play Season"
          : "▶ Play";
  const primaryActionLabel = hasLocalPartChoices
    ? "Choose Part"
    : hasLocalVersionChoices
      ? "Choose Version"
      : primaryPlayLabel;
  const currentSeriesTitle = attrs.series_title ?? (attrs.item_type === "show" ? attrs.title : null);
  const artworkContext = seriesContext ? "series" : undefined;
  const heroBackdropSrc = itemArtworkSrc(item.data.data, "backdrop", { width: 1920, format: "webp", context: artworkContext });
  const posterSrc = itemArtworkSrc(item.data.data, "poster", { width: 300, format: "webp", context: artworkContext });
  const displayTitle = pageKind === "show"
    ? (currentSeriesTitle ?? attrs.title)
    : pageKind === "season"
      ? formatSeasonDisplayLabel(attrs.season_number, attrs.season_title ?? attrs.title)
      : attrs.title;
  const displayType = pageKind === "show" ? "series" : pageKind;
  const showLikePage = pageKind === "show";
  const seasonLikePage = pageKind === "season";
  const episodeLikePage = pageKind === "episode";
  const seriesPageHref = currentItem ? seriesHrefForItem(currentItem) : null;
  const seasonPageHref = currentItem ? seasonHrefForItem(currentItem) : null;
  const supportsDisplayOverrides = attrs.item_type === "movie" || attrs.item_type === "episode";
  const seriesSeasonCount = attrs.season_count ?? seasons.length;
  const seriesEpisodeCount = attrs.episode_count ?? seasons.reduce((total, season) => total + season.episodeCount, 0);
  const seriesSummary = showLikePage
    ? `${seriesSeasonCount} season${seriesSeasonCount === 1 ? "" : "s"} • ${seriesEpisodeCount} episode${seriesEpisodeCount === 1 ? "" : "s"}`
    : null;
  const seasonSummary = seasonLikePage && activeSeason
    ? `${activeSeason.episodeCount} episode${activeSeason.episodeCount === 1 ? "" : "s"}`
    : null;
  const activeSeasonSubtitle = activeSeason
    ? `${activeSeason.episodeCount} episode${activeSeason.episodeCount === 1 ? "" : "s"}`
    : "Season and episode navigation appears when TV metadata is available.";
  const watchTargetItems = showLikePage
    ? allEpisodes
    : seasonLikePage
      ? activeSeason?.episodes ?? []
      : currentItem && ["movie", "episode"].includes(currentItem.attributes.item_type)
        ? [currentItem]
        : [];
  const watchToggleItemId = showLikePage
    ? attrs.item_type === "show"
      ? item.data.data.id
      : attrs.series_id ?? item.data.data.id
    : seasonLikePage
      ? activeSeason?.routeItemId ?? attrs.season_id ?? item.data.data.id
      : item.data.data.id;
  const watchTargetItemCount = watchState?.attributes.item_count ?? watchTargetItems.length;
  const watchedTargetCount = currentItem && currentItem.id in watchOverrides
    ? (watchOverrides[currentItem.id] ? watchTargetItemCount : 0)
    : watchState?.attributes.watched_count
      ?? watchTargetItems.filter((targetItem) => isWatched(targetItem)).length;
  const allTargetsWatched = currentItem && currentItem.id in watchOverrides
    ? watchOverrides[currentItem.id]!
    : watchState
      ? watchState.attributes.watched
      : watchTargetItems.length > 0 && watchedTargetCount === watchTargetItems.length;
  const watchScopeLabel = showLikePage
    ? "Series"
    : seasonLikePage
      ? "Season"
      : attrs.item_type === "episode"
        ? "Episode"
        : attrs.item_type === "movie"
          ? "Movie"
          : "Item";
  const canTogglePlayed = (watchState
    ? (showLikePage || seasonLikePage || watchState.attributes.can_mark_watched)
    : watchTargetItems.length > 0)
    && !watchStatesInitialLoad;
  const watchProgressSummary = watchTargetItemCount > 1
    ? `${watchedTargetCount}/${watchTargetItemCount} watched`
    : null;
  const scanActions: { id: string; label: string }[] = [];
  const skipMarkerScanActions: { id: string; label: string }[] = [];
  const addScanAction = (id: string | null | undefined, label: string) => {
    if (!id || scanActions.some((action) => action.id === id)) return;
    scanActions.push({ id, label });
  };
  const addSkipMarkerScanAction = (id: string | null | undefined, label: string) => {
    if (!id || skipMarkerScanActions.some((action) => action.id === id)) return;
    skipMarkerScanActions.push({ id, label });
  };

  switch (attrs.item_type) {
    case "movie":
      addScanAction(itemId, "Scan Movie");
      addSkipMarkerScanAction(itemId, "Scan Movie Skip Markers");
      break;
    case "show":
      addScanAction(itemId, "Scan Series");
      addScanAction(activeSeason?.castItemId, activeSeason ? `Scan ${activeSeason.label}` : "Scan Season");
      addSkipMarkerScanAction(itemId, "Scan Series Skip Markers");
      addSkipMarkerScanAction(activeSeason?.castItemId, activeSeason ? `Scan ${activeSeason.label} Skip Markers` : "Scan Season Skip Markers");
      break;
    case "season":
      addScanAction(attrs.series_id, "Scan Series");
      addScanAction(itemId, "Scan Season");
      addSkipMarkerScanAction(attrs.series_id, "Scan Series Skip Markers");
      addSkipMarkerScanAction(itemId, "Scan Season Skip Markers");
      break;
    case "episode":
      addScanAction(attrs.series_id, "Scan Series");
      addScanAction(attrs.season_id, "Scan Season");
      addScanAction(itemId, "Scan Episode");
      addSkipMarkerScanAction(attrs.series_id, "Scan Series Skip Markers");
      addSkipMarkerScanAction(attrs.season_id, "Scan Season Skip Markers");
      addSkipMarkerScanAction(itemId, "Scan Episode Skip Markers");
      break;
    default:
      break;
  }

  function handleOpenMetadataEditor() {
    setMetadataEditorOpen(true);
    setActionsMenuOpen(false);
    requestAnimationFrame(() => {
      metadataSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function handleEditFile() {
    setActionsMenuOpen(false);
    if (!itemId) return;
    const next = window.prompt(
      "New media file path for this item (must be inside the library folder):",
      "",
    );
    if (!next || !next.trim()) return;
    try {
      await updateItem(itemId, { source_path: next.trim() });
      await item.refetch();
    } catch (err) {
      window.alert(`Could not repoint the file: ${err instanceof Error ? err.message : "unknown error"}`);
    }
  }

  async function runSubtitleSearch(lang: string) {
    if (!itemId) return;
    setSubtitleLoading(true);
    setSubtitleError(null);
    setSubtitleStatus(null);
    try {
      const response = await searchExternalSubtitles(itemId, lang);
      setSubtitleResults(response.data);
    } catch (err) {
      setSubtitleResults([]);
      setSubtitleError(err instanceof Error ? err.message : "Failed to search subtitles");
    } finally {
      setSubtitleLoading(false);
    }
  }

  async function handleOpenSubtitleDialog() {
    setActionsMenuOpen(false);
    setSubtitleDialogOpen(true);
    const lang = subtitleLanguage || opensubtitlesDefaultLanguage || "en";
    setSubtitleLanguage(lang);
    await runSubtitleSearch(lang);
  }

  async function handleDownloadSubtitle(result: SubtitleProviderResult) {
    if (!itemId) return;
    setSubtitleDownloadingId(result.id);
    setSubtitleError(null);
    setSubtitleStatus(null);
    try {
      await downloadExternalSubtitle(itemId, {
        file_id: result.attributes.file_id,
        language: result.attributes.language || subtitleLanguage,
        file_name: result.attributes.file_name,
      });
      setSubtitleStatus(`Saved ${result.attributes.language.toUpperCase()} subtitle. Reload the player track list to see it.`);
    } catch (err) {
      setSubtitleError(err instanceof Error ? err.message : "Failed to download subtitle");
    } finally {
      setSubtitleDownloadingId(null);
    }
  }

  function handleCloseSubtitleDialog() {
    setSubtitleDialogOpen(false);
    setSubtitleResults([]);
    setSubtitleError(null);
    setSubtitleStatus(null);
  }

  function handlePrimaryPlayAction() {
    if (attrs.item_type === "movie" && (hasLocalPlaybackChoices || (localMedia.loading && !localMedia.data))) {
      localMediaSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    void handlePlay(playTargetId, { startPositionMS: hasResume ? resumePositionMS : undefined });
  }

  return (
    <div className="item-page">
      <div className="item-hero">
        <img
          className="item-hero-backdrop"
          src={heroBackdropSrc}
          alt=""
        />
        <div className="item-hero-gradient" />
      </div>

      <div className="item-detail">
        <img
          className="item-poster"
          src={posterSrc}
          alt={displayTitle}
        />

        <div className="item-info">
          {currentSeriesTitle && currentSeriesTitle !== displayTitle && (
            seriesPageHref ? (
              <Link className="item-kicker item-kicker--link" to={seriesPageHref}>
                {currentSeriesTitle}
              </Link>
            ) : (
              <p className="item-kicker">{currentSeriesTitle}</p>
            )
          )}
          <h1 className="item-title">{displayTitle}</h1>

          {attrs.source_available === false && (
            <div className="item-source-missing" role="alert">
              The media file for this title is missing or unreachable. Playback is
              unavailable until the file is restored or repointed.
            </div>
          )}

          <div className="item-meta-row">
            {currentItem && <RatingBadge item={currentItem} placement="detail" />}
            {episodeLikePage && itemCode && <span className="item-meta-item">{itemCode}</span>}
            {episodeLikePage && attrs.season_title && (
              seasonPageHref ? (
                <Link className="item-meta-item item-meta-link" to={seasonPageHref}>
                  {attrs.season_title}
                </Link>
              ) : (
                <span className="item-meta-item">{attrs.season_title}</span>
              )
            )}
            {releaseDate && <span className="item-meta-item">{releaseDate}</span>}
            {!releaseDate && attrs.year && <span className="item-meta-item">{attrs.year}</span>}
            {attrs.collection_resource_id && attrs.collection_name && (
              <Link className="item-meta-item item-meta-link" to={`/collections/${attrs.collection_resource_id}`}>
                Part of {attrs.collection_name}
              </Link>
            )}
            {seriesSummary && <span className="item-meta-item">{seriesSummary}</span>}
            {seasonSummary && <span className="item-meta-item">{seasonSummary}</span>}
            {!showLikePage && attrs.duration_ms && (
              <span className="item-meta-item">{formatDurationMS(attrs.duration_ms)}</span>
            )}
            <span className="badge">{displayType}</span>
            {watchProgressSummary && (
              <span className="item-meta-item">{watchProgressSummary}</span>
            )}
            {!watchProgressSummary && hasResume && (
              <span className="item-meta-item">{Math.round(progressPercent)}% watched</span>
            )}
          </div>

          <div className="item-actions">
            <button
              className="btn btn--primary btn--large"
              onClick={handlePrimaryPlayAction}
              disabled={starting || !playTargetId}
            >
              {starting ? "Starting..." : primaryActionLabel}
            </button>
            {hasResume && (
              <button
                className="btn btn--secondary"
                onClick={() => void handlePlay(playTargetId, { startPositionMS: 0 })}
                disabled={starting || !playTargetId}
              >
                Start Over
              </button>
            )}
            {downloadPath && isStandalone && (
              // iOS forbids downloads entirely in home-screen standalone mode
              // (no Safari chrome, no download manager to catch the
              // response), so the control has to break out to real Safari
              // instead of navigating in place (#733).
              <div className="item-download-standalone">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    window.open(downloadPath, "_blank");
                    setDownloadStarted(true);
                  }}
                >
                  {downloadLabel}
                </button>
                <span className="item-download-standalone-hint">Opens in Safari to download</span>
              </div>
            )}
            {downloadPath && !isStandalone && (
              // No `download` attribute: af84b225 added it deliberately, but
              // #733 confirmed it was never the fix — Content-Disposition:
              // attachment is the sole standard trigger, and the attribute
              // only matters (badly) in the standalone case handled above.
              <a
                className="btn btn--secondary"
                href={downloadPath}
                onClick={() => setDownloadStarted(true)}
              >
                {downloadLabel}
              </a>
            )}
            {canToggleWatchlist && (
              <button
                className="btn btn--secondary"
                type="button"
                onClick={() => void handleToggleWatchlist()}
                disabled={watchlistUpdating || watchlist.loading}
              >
                {watchlistUpdating ? "Updating..." : currentItemWatchlisted ? "In Watchlist" : "Add to Watchlist"}
              </button>
            )}
            {canTogglePlayed && (
              <WatchToggleButton
                className={`btn btn--ghost item-watch-toggle${allTargetsWatched ? " item-watch-toggle--watched" : ""}${watchUpdating ? " item-watch-toggle--pending" : ""}`}
                watched={allTargetsWatched}
                updating={watchUpdating || watchStatesInitialLoad}
                watchedLabel={`Mark ${watchScopeLabel.toLowerCase()} as watched`}
                unwatchedLabel={`Mark ${watchScopeLabel.toLowerCase()} as unwatched`}
                onToggle={() => void handleTogglePlayed()}
              />
            )}
            <div className="item-actions-menu" ref={actionsMenuRef}>
              <button
                type="button"
                className="btn btn--ghost"
                title="More options"
                aria-haspopup="menu"
                aria-expanded={actionsMenuOpen}
                onClick={() => setActionsMenuOpen((open) => !open)}
              >
                ⋯
              </button>
              {actionsMenuOpen && (
                <div className="item-actions-dropdown" role="menu">
                  <button
                    type="button"
                    className="item-actions-dropdown-item"
                    role="menuitem"
                    onClick={handleOpenMetadataEditor}
                  >
                    Edit Metadata
                  </button>
                  {user?.attributes.is_admin && (attrs.item_type === "movie" || attrs.item_type === "episode") && (
                    <button
                      type="button"
                      className="item-actions-dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        void handleEditFile();
                      }}
                    >
                      Edit File
                    </button>
                  )}
                  {opensubtitlesEnabled && (attrs.item_type === "movie" || attrs.item_type === "episode") && (
                    <button
                      type="button"
                      className="item-actions-dropdown-item"
                      role="menuitem"
                      onClick={handleOpenSubtitleDialog}
                    >
                      Find Subtitles…
                    </button>
                  )}
                  {(attrs.item_type === "movie" || attrs.item_type === "episode") && (
                    <button
                      type="button"
                      className="item-actions-dropdown-item"
                      role="menuitem"
                      disabled={markBadPending}
                      onClick={() => {
                        setActionsMenuOpen(false);
                        setMarkBadError(null);
                        setMarkBadMessage("Requesting a replacement…");
                        setMarkBadPending(true);
                        void markItemBad(itemId!)
                          .then((res) => {
                            const a = res.data.attributes;
                            setMarkBadMessage(a.message || `Replacement: ${a.state}`);
                          })
                          .catch((err: unknown) => {
                            setMarkBadMessage(null);
                            setMarkBadError(err instanceof Error ? err.message : "Failed to mark as bad");
                          })
                          .finally(() => setMarkBadPending(false));
                      }}
                    >
                      {markBadPending ? "Reporting…" : "Report bad copy"}
                    </button>
                  )}
                  {scanActions.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className="item-actions-dropdown-item"
                      role="menuitem"
                      disabled={scanTargetId !== null}
                      onClick={() => {
                        setActionsMenuOpen(false);
                        void handleScopedScan(action.id, action.label);
                      }}
                    >
                      {scanTargetId === action.id ? `${action.label}...` : action.label}
                    </button>
                  ))}
                  {skipMarkerScanActions.map((action) => {
                    const pendingId = `skip:${action.id}`;
                    return (
                      <button
                        key={pendingId}
                        type="button"
                        className="item-actions-dropdown-item"
                        role="menuitem"
                        disabled={scanTargetId !== null}
                        onClick={() => {
                          setActionsMenuOpen(false);
                          void handleSkipMarkerScan(action.id, action.label);
                        }}
                      >
                        {scanTargetId === pendingId ? `${action.label}...` : action.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {downloadStarted && isStandalone && (
            <p className="settings-status settings-status--success item-status-message" role="status">
              {downloadSizeBytes === null
                ? "Download started — check Safari's downloads. "
                : `Download started (${formatByteSize(downloadSizeBytes)}) — check Safari's downloads. `}
              It opened in Safari, separate from this app, so it keeps running even if
              you switch back here.
            </p>
          )}
          {downloadStarted && !isStandalone && (
            <p className="settings-status settings-status--success item-status-message" role="status">
              {downloadSizeBytes === null
                ? "Download started. "
                : `Download started (${formatByteSize(downloadSizeBytes)}). `}
              It continues in your browser&apos;s downloads. Leaving this page or switching
              apps before it finishes cancels the transfer.
            </p>
          )}
          {scanMessage && <p className="settings-status settings-status--success item-status-message">{scanMessage}</p>}
          {scanError && <p className="settings-status settings-status--error item-status-message">{scanError}</p>}
          {markBadMessage && <p className="settings-status settings-status--success item-status-message">{markBadMessage}</p>}
          {markBadError && <p className="settings-status settings-status--error item-status-message">{markBadError}</p>}
          <div className="item-watch-status-slot" aria-live="polite">
            {watchMessage && <p className="settings-status settings-status--success item-status-message">{watchMessage}</p>}
            {watchError && <p className="settings-status settings-status--error item-status-message">{watchError}</p>}
          </div>
          {watchlistMessage && <p className="settings-status settings-status--success item-status-message">{watchlistMessage}</p>}
          {watchlistError && <p className="settings-status settings-status--error item-status-message">{watchlistError}</p>}

          {hasResume && (
            <p className="item-overview">Resume from {formatPlaybackPosition(resumePositionMS)}.</p>
          )}

          {attrs.overview && (
            <p className="item-overview">{attrs.overview}</p>
          )}
        </div>
      </div>

      {showLikePage && (
        <SeasonDirectorySection
          seasons={seasons}
          watchUpdatingKey={watchUpdatingKey}
          starting={starting}
          onPlaySeason={(season) => void handlePlay(season.firstEpisodeId ?? season.episodes[0]?.id ?? null)}
          onToggleSeasonWatched={(season) => void handleToggleSeasonWatched(season)}
          isSeasonWatched={isSeasonWatched}
          canToggleSeasonWatched={canToggleSeasonWatched}
          seasonProgress={seasonProgress}
        />
      )}

      {seasonLikePage && (
        <>
          {(previousEpisode || nextEpisode) && (
            <EpisodeNavigationSection
              previousEpisode={previousEpisode}
              nextEpisode={nextEpisode}
              seasonPageHref={seriesPageHref}
            />
          )}

          <EpisodeCollectionSection
            title="Episodes"
            subtitle={activeSeasonSubtitle}
            seasons={seasons}
            activeSeason={activeSeason}
            episodeCollectionLoading={episodeCollection.loading}
            episodeCollectionError={Boolean(episodeCollection.error)}
            watchUpdatingKey={watchUpdatingKey}
            seasonSelectorRef={seasonSelectorRef}
            onScrollSeasons={scrollSeasonSelector}
            onPlayEpisode={(episodeId) => void handlePlay(episodeId)}
            onToggleEpisodeWatched={(episode) => void handleToggleEpisodeWatched(episode)}
            isEpisodeWatched={isEpisodeWatched}
            canToggleEpisodeWatched={canToggleEpisodeWatched}
            episodeProgress={episodeProgress}
            starting={starting}
          />
        </>
      )}

      {episodeLikePage && (
        <>
          <EpisodeNavigationSection
            previousEpisode={previousEpisode}
            nextEpisode={nextEpisode}
            seasonPageHref={seasonPageHref}
          />

          <EpisodeCollectionSection
            title="More In This Season"
            subtitle={activeSeasonSubtitle}
            seasons={seasons}
            activeSeason={activeSeason}
            activeEpisodeId={currentItem?.id}
            episodeCollectionLoading={episodeCollection.loading}
            episodeCollectionError={Boolean(episodeCollection.error)}
            watchUpdatingKey={watchUpdatingKey}
            seasonSelectorRef={seasonSelectorRef}
            onScrollSeasons={scrollSeasonSelector}
            onPlayEpisode={(episodeId) => void handlePlay(episodeId)}
            onToggleEpisodeWatched={(episode) => void handleToggleEpisodeWatched(episode)}
            isEpisodeWatched={isEpisodeWatched}
            canToggleEpisodeWatched={canToggleEpisodeWatched}
            episodeProgress={episodeProgress}
            starting={starting}
          />
        </>
      )}

      {currentItem && attrs.item_type === "movie" && (
        <LocalMediaSection
          item={currentItem}
          children={localMediaItems}
          loading={localMedia.loading}
          error={localMedia.error}
          starting={starting}
          sectionRef={localMediaSectionRef}
          onPlay={(targetItemId) => void handlePlay(targetItemId)}
        />
      )}

      {!cast.loading && cast.data && cast.data.data.length > 0 && (
        <CastRail members={cast.data.data} />
      )}

      {!similarItems.loading && similarItems.data && (
        <MoreLikeThisSection items={similarItems.data.data} />
      )}

      {metadataEditorOpen && (
      <section className="metadata-editor" ref={metadataSectionRef}>
        <div className="item-section-heading">
          <div>
            <h2 className="item-section-title">Metadata Override</h2>
            <p className="item-section-subtitle">Pick a better TMDb match or save display overrides for this title.</p>
          </div>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => setMetadataEditorOpen(false)}
          >
            Done
          </button>
        </div>

          <div className="metadata-editor-panel">
            <div className="metadata-editor-grid">
              <label className="settings-field">
                <span>TMDb Search</span>
                <input
                  type="text"
                  value={metadataSearchQuery}
                  onChange={(event) => setMetadataSearchQuery(event.target.value)}
                  placeholder="Search title"
                />
              </label>

              <label className="settings-field">
                <span>TMDb ID</span>
                <input
                  type="text"
                  value={metadataTMDbID}
                  onChange={(event) => setMetadataTMDbID(event.target.value)}
                  placeholder="Enter a TMDb ID"
                />
              </label>
            </div>

            <div className="metadata-editor-actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void handleMetadataSearch()}
                disabled={metadataSearching || metadataSaving}
              >
                {metadataSearching ? "Searching..." : "Search TMDb"}
              </button>
            </div>

            {metadataMatches.length > 0 && (
              <div className="metadata-match-list">
                {metadataMatches.map((match) => (
                  <article key={match.id} className="metadata-match-card">
                    <div className="metadata-match-art">
                      {match.attributes.poster_url ? (
                        <img src={match.attributes.poster_url} alt={match.attributes.title} loading="lazy" />
                      ) : (
                        <div className="metadata-match-art-placeholder">TMDb</div>
                      )}
                    </div>
                    <div className="metadata-match-copy">
                      <div className="metadata-match-title-row">
                        <div>
                          <h3 className="metadata-match-title">
                            {match.attributes.title}
                            {match.attributes.year ? ` (${match.attributes.year})` : ""}
                          </h3>
                          {match.attributes.original_title && match.attributes.original_title !== match.attributes.title && (
                            <p className="metadata-match-original">{match.attributes.original_title}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={metadataSaving}
                          onClick={() => void handleApplyMetadataMatch(match.attributes.tmdb_id)}
                        >
                          Apply Match
                        </button>
                      </div>
                      <p className="metadata-match-overview">{match.attributes.overview ?? "No overview available."}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {supportsDisplayOverrides ? (
              <>
                <div className="metadata-editor-grid metadata-editor-grid--stacked">
                  <label className="settings-field">
                    <span>Display Title</span>
                    <input
                      type="text"
                      value={metadataTitle}
                      onChange={(event) => setMetadataTitle(event.target.value)}
                    />
                  </label>

                  <label className="settings-field">
                    <span>Display Year</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={metadataYear}
                      onChange={(event) => setMetadataYear(event.target.value)}
                      placeholder="Leave blank to use scanned/TMDb year"
                    />
                  </label>

                  <label className="settings-field">
                    <span>Release / air date</span>
                    <input
                      type="date"
                      value={metadataReleaseDate}
                      onChange={(event) => setMetadataReleaseDate(event.target.value)}
                      placeholder="YYYY-MM-DD; leave blank to use scanned/TMDb date"
                    />
                  </label>

                  <label className="settings-field settings-field--full">
                    <span>Overview</span>
                    <textarea
                      className="settings-textarea"
                      value={metadataOverview}
                      onChange={(event) => setMetadataOverview(event.target.value)}
                      rows={5}
                    />
                  </label>
                </div>

                <div className="metadata-editor-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => void handleSaveMetadataOverrides()}
                    disabled={metadataSaving}
                  >
                    {metadataSaving ? "Saving..." : "Save Overrides"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => void handleClearMetadataOverrides()}
                    disabled={metadataSaving}
                  >
                    Clear Overrides
                  </button>
                </div>
              </>
            ) : (
              <p className="settings-help">
                Manual title, year, and overview overrides are currently available for movies and episodes. Series and seasons support TMDb match correction here.
              </p>
            )}

            {metadataMessage && <p className="settings-status settings-status--success">{metadataMessage}</p>}
            {metadataError && <p className="settings-status settings-status--error">{metadataError}</p>}
          </div>
      </section>
      )}

      {subtitleDialogOpen && (
        <div className="subtitle-search-overlay" role="dialog" aria-modal="true" onClick={handleCloseSubtitleDialog}>
          <div className="subtitle-search-panel" onClick={(event) => event.stopPropagation()}>
            <div className="subtitle-search-header">
              <h2>Find Subtitles</h2>
              <IconButton
                type="button"
                className="btn btn--ghost"
                align="end"
                onClick={handleCloseSubtitleDialog}
                label="Close"
              >
                ✕
              </IconButton>
            </div>

            <div className="subtitle-search-controls">
              <label className="settings-field">
                <span>Language</span>
                <input
                  type="text"
                  value={subtitleLanguage}
                  onChange={(event) => setSubtitleLanguage(event.target.value)}
                  placeholder="en"
                  spellCheck={false}
                />
              </label>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void runSubtitleSearch(subtitleLanguage)}
                disabled={subtitleLoading}
              >
                {subtitleLoading ? "Searching…" : "Search"}
              </button>
            </div>

            {subtitleError && <p className="settings-status settings-status--error">{subtitleError}</p>}
            {subtitleStatus && <p className="settings-status settings-status--success">{subtitleStatus}</p>}

            <div className="subtitle-search-results">
              {!subtitleLoading && subtitleResults.length === 0 && !subtitleError && (
                <p className="settings-help">No results yet. Try another language.</p>
              )}
              {subtitleResults.map((result) => (
                <div key={result.id} className="subtitle-search-row">
                  <div className="subtitle-search-row-main">
                    <span className="subtitle-search-row-title">{result.attributes.release || result.attributes.file_name || `Subtitle #${result.attributes.file_id}`}</span>
                    <span className="subtitle-search-row-meta">
                      {result.attributes.language?.toUpperCase() ?? "??"}
                      {result.attributes.hearing_impaired ? " · SDH" : ""}
                      {typeof result.attributes.download_count === "number" ? ` · ${result.attributes.download_count.toLocaleString()} dl` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={subtitleDownloadingId === result.id}
                    onClick={() => void handleDownloadSubtitle(result)}
                  >
                    {subtitleDownloadingId === result.id ? "Saving…" : "Download"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
